import { useCallback, useEffect, useRef } from 'react';
import { ROUTES } from '../constants/routes';

export const SOUND_NOTIFICATION_LS_DISABLED = 'SOUND_NOTIFICATION_DT_KEY';

interface NotificationSoundPayload {
  chatId?: string;
  chatString?: string;
  messageId?: string;
  senderType?: string;
  type?: string;
}

interface NotificationSoundEventData {
  id?: string;
  payload?: NotificationSoundPayload;
  type?: string;
}

const usePlayNotificationSound = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const messageIdSet = useRef<Set<string>>(new Set());

  function isDTVisible() {
    return document.visibilityState === 'visible';
  }

  const isCurrentConversationNotification = useCallback((payload?: NotificationSoundPayload) => {
    if (!payload || !window.location.pathname?.includes(ROUTES.CONVERSATIONS)) {
      return false;
    }

    try {
      const chat = payload.chatString ? JSON.parse(payload.chatString) : {};
      const currentRouteToken = window.location.pathname.split('/').filter(Boolean).pop();
      const notificationRouteTokens = [payload.chatId, chat.phoneNumber, chat.chatTypeId].filter(Boolean);

      return !!currentRouteToken && notificationRouteTokens.includes(currentRouteToken);
    } catch (error) {
      return false;
    }
  }, []);

  const isSoundEnabled = useCallback(
    (payload?: NotificationSoundPayload) => {
      const isSoundDisabled = window.localStorage.getItem(SOUND_NOTIFICATION_LS_DISABLED) === 'true';
      const isVisible = isDTVisible();
      const isConversationRoute = window.location.pathname?.includes(ROUTES.CONVERSATIONS);
      const isCurrentConversation = isCurrentConversationNotification(payload);

      if (!isSoundDisabled && !isVisible) {
        return true;
      }

      return !isSoundDisabled && (!isConversationRoute || !isCurrentConversation);
    },
    [isCurrentConversationNotification]
  );

  const playSoundFromEvent = useCallback(
    (eventData: NotificationSoundEventData) => {
      const messageId = eventData.id;
      const payload = eventData.payload;

      if (messageId === undefined || !isSoundEnabled(payload) || messageIdSet.current.has(messageId)) {
        return;
      }

      messageIdSet.current.add(messageId);

      if (messageIdSet.current.size > 20) {
        const iterator = messageIdSet.current.values();
        messageIdSet.current.delete(iterator.next().value ?? '');
      }

      const audio = audioRef.current;
      if (audio && audio.paused) {
        audio.play().catch((error) => {
          console.error('Sound playback failed:', error);
        });
      }
    },
    [isSoundEnabled]
  );

  const playNotificationSound = (event: MessageEvent) => {
    if (event.data && event.data.type === 'DT_PLAY_SOUND') {
      playSoundFromEvent(event.data);
    }
  };

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio('/notification-alert.mp3'); // Path to your sound file
    }

    navigator.serviceWorker.addEventListener('message', playNotificationSound);

    return () => {
      navigator.serviceWorker.removeEventListener('message', playNotificationSound);
    };
  }, [playSoundFromEvent]);

  useEffect(() => {
    window.addEventListener('message', playNotificationSound);

    return () => {
      window.removeEventListener('message', playNotificationSound);
    };
  }, [playSoundFromEvent]);

  return null;
};

export default usePlayNotificationSound;
